import React from 'react';

import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PatientActivity } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService, DateObject } from '@uplift/services';

import { ActivityItem } from './ActivityItem.component';

export type ActivityListProps = {
  patientId: string;
};


const GroupTitle: React.VFC<{
  prevActivityDate?: string;
  date: string;
  now: DateObject;
}> = ({ prevActivityDate: _prevActivityDate, date: _date, now }) => {
  const { classes } = useStyles();
  const timezone = useTimezone();
  const prevActivityDate = React.useMemo(() => _prevActivityDate && CalendarDateService.date(_prevActivityDate).tz(timezone), [ _prevActivityDate, timezone ]);
  const date = React.useMemo(() => CalendarDateService.date(_date).tz(timezone), [ _date, timezone ]);
  const shouldRenderTitle = React.useMemo(() => {
    if (!prevActivityDate) return true;

    const isSameDate = prevActivityDate.isSame(date, 'date');

    return !isSameDate;
  }, [ date, prevActivityDate ]);

  const title = React.useMemo(() => {
    if (!shouldRenderTitle) {
      return null;
    }

    if (now.isSame(date, 'year')) {
      return date.format('MMM D');
    }

    return date.format('MMM D, YYYY');
  }, [ date, now, shouldRenderTitle ]);

  if (!shouldRenderTitle) {
    return null;
  }

  return (
    <Typography variant="overline" color="textSecondary" className={classes.title}>
      {title}
    </Typography>
  );
};


export const ActivityPage: React.VFC<{
  now: DateObject;
  activities: PatientActivity[];
  prevActivities: PatientActivity[];
  onItemSelect: (el?: PatientActivity) => void;
}> = React.memo(({ prevActivities, now, activities, onItemSelect }) => {
  return (
    <>
      {activities.map((activity, index, arr) => {
        const prevActivityDate = index === 0 ? prevActivities?.[prevActivities.length - 1]?.datetime : arr[index - 1]?.datetime;

        return (
          <React.Fragment key={activity.datetime}>
            <GroupTitle
              prevActivityDate={prevActivityDate}
              now={now}
              date={activity.datetime}
            />
            <ActivityItem
              onActivityClick={() => onItemSelect(activity)}
              onDocumentClick={() => onItemSelect(activity)}
              activity={activity}
            />
          </React.Fragment>
        );
      })}
    </>
  );
});

const useStyles = makeStyles()(theme => ({
  title: {
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(1),
    }
  }
}));
